/**
 * src/core/telemetry/events.ts — typed analytics events used INSIDE core
 * (PRI-439 R14). Same names/signatures the host analytics module exposes, but
 * dispatched through the injected TelemetryTracker — core has zero imports of
 * PostHog/GA4/first-party analytics. Default tracker is a no-op; the host app
 * injects the real dispatcher (src/primate/telemetry-bridge).
 */
import { getTelemetry } from './TelemetryProvider'

const t = () => getTelemetry().tracker

// ── Upload / analysis pipeline ────────────────────────────────────────────────

export function trackVideoUploaded(props: {
  file_size_bytes: number; file_size_mb: number; filename: string
  user_plan: string; is_authenticated: boolean
}): void { t().track('video_uploaded', props) }

export function trackVideoAnalysed(props: {
  prompt: string; prompt_is_custom: boolean; source: 'fresh' | 'run_prompt' | 'cache' | 'demo'
  user_plan: string; is_authenticated: boolean
}): void { t().track('video_analysed', props) }

export function trackAnalysisResult(props: {
  answer: 'Yes' | 'No' | null; query_type: string | null
  has_confidence: boolean; user_plan: string
}): void { t().track('analysis_result', props) }

export function trackAnalysisCancelled(user_plan: string): void {
  t().track('analysis_cancelled', { user_plan })
}

export function trackUserVideoSaved(props: {
  filename: string; file_size_bytes: number; user_plan: string
}): void { t().track('user_video_saved', props) }

export function trackVideoRenamed(props: { video_id: string; user_plan: string }): void {
  t().track('video_renamed', props)
}

export function trackDemoQuotaHit(): void { t().track('demo_quota_hit') }

// ── Prompt compile / query parsing ────────────────────────────────────────────

export function trackQueryParsed(props: {
  parse_mode: 'haiku' | 'heuristic'; prompt_length: number; duration_ms: number
}): void { t().track('query_parsed', props) }

export function trackQueryParseFailed(props: {
  error_type: 'parse_failed' | 'network_error'; prompt_length: number; status?: number
}): void { t().track('query_parse_failed', props) }

export function trackCompiledQueryEdited(props: {
  action: 'add_lane' | 'restore' | 'edit_toggle_open' | 'edit_toggle_close' | 'edit_term' | 'delete_lane'
  lane_count?: number
  lane_type?: string
  user_plan?: string
}): void { t().track('compiled_query_edited', props) }

export function trackBatchModeToggled(props: { enabled: boolean; user_plan: string }): void {
  t().track('batch_mode_toggled', props)
}

// ── Result pill interactions ──────────────────────────────────────────────────

export function trackResultCopied(props: { user_plan: string }): void {
  t().track('result_copied', props)
}

export function trackResultRerun(props: { user_plan: string }): void {
  t().track('result_rerun', props)
}

// ── Onboarding / empty-state demo ─────────────────────────────────────────────

export function trackCoachDismissed(props: { type: 'session' | 'persistent' }): void {
  t().track('coach_dismissed', props)
}

export function trackCompareModalOpened(props?: Record<string, unknown>): void {
  t().track('compare_modal_opened', props)
}

export function trackOnboardingVideoWatched(props?: Record<string, unknown>): void {
  t().track('onboarding_video_watched', props)
}

export function trackTryForYourselfClicked(props?: Record<string, unknown>): void {
  t().track('try_for_yourself_clicked', props)
}

// ── Streaming ─────────────────────────────────────────────────────────────────

export function trackStreamStarted(props: {
  prompt: string; user_plan: string; camera_label: string; resolution: string
}): void { t().track('stream_started', props) }

export function trackStreamQueued(props: { position: number; user_plan: string }): void {
  t().track('stream_queued', props)
}

export function trackStreamLive(props: { user_plan: string } & Record<string, unknown>): void {
  t().track('stream_live', props)
}

export function trackStreamStopped(props: {
  duration_s: number; stop_reason: string; user_plan: string
} & Record<string, unknown>): void { t().track('stream_stopped', props) }

export function trackStreamQuotaWarning(props: { remaining_s: number; user_plan: string }): void {
  t().track('stream_quota_warning', props)
}

export function trackStreamQuotaExceeded(props: { user_plan: string }): void {
  t().track('stream_quota_exceeded', props)
}

export function trackStreamError(props: { error_code: string; user_plan: string } & Record<string, unknown>): void {
  t().track('stream_error', props)
}

export function trackStreamCameraDenied(props: { user_plan: string }): void {
  t().track('stream_camera_denied', props)
}

export function trackStreamPromptUpdated(props: { prompt: string; user_plan: string }): void {
  t().track('stream_prompt_updated', props)
}

// ── Narrative ────────────────────────────────────────────────────────────────────────────

export function trackNarrativeFeedback(props: {
  verdict: 'up' | 'down'; entry_index: number; session_type: 'stream' | 'upload'
}): void { t().track('narrative_feedback', props) }

// ── Billing / quota wall ─────────────────────────────────────────────────────

/** High-intent upgrade click from the quota bar (credits_exhausted = hard gate; warning_banner = soft nudge). */
export function trackUpgradeFromQuotaWall(trigger: 'credits_exhausted' | 'warning_banner' | 'anon_trial_exhausted' | 'anon_trial_low'): void {
  t().track('upgrade_from_quota_wall', { trigger })
}

// ── Mobile result card ────────────────────────────────────────────────────────

/** PRI-494 P2: fired the first time a user expands the "Raw API data" disclosure on mobile.
 *  PRI-552: optionally carries the terminal metering_rule (null when usage hasn't landed)
 *  so product can see whether per-frame-billed users inspect raw API data differently. */
export function trackRawApiTrayOpened(props: { pill_id: string; is_stream: boolean; metering_rule?: 'per_second' | 'per_frame' | null }): void {
  t().track('raw_api_tray_opened', props)
}

// ── Mobile session history (PRI-494 P3) ───────────────────────────────────────

/** Fired when the Session History overlay is opened via the clock icon. */
export function trackMobileHistoryOpened(props: { session_count: number }): void {
  t().track('mobile_history_opened', props)
}

/** Fired when a session row is tapped to reopen. */
export function trackMobileSessionReopened(props: { source: 'live' | 'upload'; persisted: boolean }): void {
  t().track('mobile_session_reopened', props)
}

/** Fired when the All/Live/Uploads filter chip is changed. */
export function trackMobileHistoryFilterUsed(props: { filter: 'all' | 'live' | 'uploads' }): void {
  t().track('mobile_history_filter_used', props)
}

// ── Mobile viewfinder P4 (PRI-494) ───────────────────────────────────────────

/** Fired when the user taps the "↑ New result" chip to scroll back to the top. */
export function trackMobileNewResultChipTapped(): void {
  t().track('mobile_new_result_chip_tapped')
}

// ── Batch analysis (PRI-488) ─────────────────────────────────────────────────

/** Fired when the user clicks Run in BatchComposer and POST /v1/analyses/batch returns successfully. */
export function trackBatchAnalysisSubmitted(props: { prompt_count: number; user_plan: string }): void {
  t().track('batch_analysis_submitted', props)
}

// ── Errors ────────────────────────────────────────────────────────────────────

export function trackError(error: Error, context?: Record<string, unknown>): void {
  t().trackError(error, context)
}
